import { useEffect, useMemo, useState } from "react";
import api, { formatTL, formatDateTime } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, FileDown, Eye, Share2, FileText, Tag } from "lucide-react";
import { toast } from "sonner";
import { downloadQuotePDF } from "../../lib/pdfGenerator";
import QuoteCustomerView from "../../components/QuoteCustomerView";

const STATUS_BADGE = {
  beklemede: { label: "Beklemede", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  kabul: { label: "Kabul Edildi", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  red: { label: "Reddedildi", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  iptal: { label: "İptal", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

const emptyPart = () => ({ name: "", weight: 1 });

export default function TechQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewQuote, setViewQuote] = useState(null);
  const [step, setStep] = useState(1);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    device_brand_name: "",
    device_model: "",
    device_serial: "",
    device_type: "",
    fault_description: "",
    target_total: 0,
    labor_fee: 0,
    parts: [emptyPart()],
    footer_note: "",
  });

  const load = async () => {
    const [q, s, b] = await Promise.all([api.get("/quotes"), api.get("/settings"), api.get("/brands")]);
    setQuotes(q.data || []);
    setSettings(s.data || {});
    setBrands(b.data || []);
  };
  useEffect(() => { load(); }, []);

  const resetWizard = () => {
    setStep(1);
    setForm({
      customer_name: "",
      customer_phone: "",
      device_brand_name: "",
      device_model: "",
      device_serial: "",
      device_type: "",
      fault_description: "",
      target_total: 0,
      labor_fee: 0,
      parts: [emptyPart()],
      footer_note: settings.quote_footer_note || "",
    });
  };

  const openWizard = () => { resetWizard(); setWizardOpen(true); };

  const addPart = () => setForm({ ...form, parts: [...form.parts, emptyPart()] });
  const updatePart = (i, k, v) => {
    const np = [...form.parts]; np[i] = { ...np[i], [k]: v }; setForm({ ...form, parts: np });
  };
  const removePart = (i) => setForm({ ...form, parts: form.parts.filter((_, idx) => idx !== i) });

  // Live preview hesaplama (Adım 3'te göstereceğimiz tutarlar)
  const preview = useMemo(() => {
    const tt = Number(form.target_total) || 0;
    const lf = Math.min(Number(form.labor_fee) || 0, tt);
    const cleanParts = (form.parts || []).filter(p => (p.name || "").trim() !== "");
    if (cleanParts.length === 0) return { labor_fee: tt, parts: [] };
    const remaining = +(tt - lf).toFixed(2);
    const totalWeight = cleanParts.reduce((s, p) => s + Math.max(Number(p.weight) || 1, 0.0001), 0);
    let accum = 0;
    const out = cleanParts.map((p, i) => {
      const w = Math.max(Number(p.weight) || 1, 0.0001);
      let amount;
      if (i === cleanParts.length - 1) amount = +(remaining - accum).toFixed(2);
      else { amount = +(remaining * w / totalWeight).toFixed(2); accum += amount; }
      return { name: p.name, weight: w, amount };
    });
    return { labor_fee: lf, parts: out };
  }, [form.target_total, form.labor_fee, form.parts]);

  const canNext1 = form.customer_name.trim() && Number(form.target_total) > 0;
  const canNext2 = form.fault_description.trim() && Number(form.labor_fee) >= 0 && Number(form.labor_fee) <= Number(form.target_total);

  const save = async () => {
    try {
      const payload = {
        ...form,
        target_total: Number(form.target_total),
        labor_fee: Number(form.labor_fee),
        parts: form.parts.filter(p => (p.name || "").trim() !== "").map(p => ({
          name: p.name.trim(),
          weight: Number(p.weight) || 1,
        })),
      };
      const res = await api.post("/quotes", payload);
      toast.success("Teklif oluşturuldu");
      setWizardOpen(false);
      setViewQuote(res.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Bu teklif silinsin mi?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Silindi"); load();
  };

  const downloadPDF = async (q) => {
    try {
      await downloadQuotePDF(q, { fallbackFooter: settings.quote_footer_note });
    } catch (e) { toast.error("PDF oluşturulamadı"); }
  };

  const whatsappShare = (q) => {
    const lines = [
      `*${q.customer_name}* için Fiyat Teklifi`,
      ``,
      `Arıza: ${q.fault_description}`,
      ``,
      `*Toplam: ${formatTL(q.target_total)}*`,
      ``,
      q.footer_note || settings.quote_footer_note || "",
    ].filter(Boolean).join("\n");
    const phone = (q.customer_phone || "").replace(/\D/g, "");
    const url = phone
      ? `https://wa.me/${phone.startsWith("90") ? phone : "9" + phone}?text=${encodeURIComponent(lines)}`
      : `https://wa.me/?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-slate-900">Fiyat Teklifi</h1>
          <p className="text-xs text-slate-500 mt-0.5">Müşteriye gösterilebilir profesyonel teklif oluşturun</p>
        </div>
        <Button onClick={openWizard} data-testid="new-quote-button">
          <Plus className="h-4 w-4 mr-2" /> Yeni
        </Button>
      </div>

      {quotes.length === 0 && (
        <Card className="p-8 text-center border-dashed border-2 border-slate-200 shadow-none">
          <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Henüz teklif yok. Yeni butonuyla başlayın.</p>
        </Card>
      )}

      <div className="space-y-2">
        {quotes.map(q => (
          <Card key={q.id} className="p-4 border border-slate-200 shadow-none" data-testid={`quote-card-${q.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-blue-700 font-bold">{q.quote_number}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[q.status]?.cls}`}>
                    {STATUS_BADGE[q.status]?.label}
                  </span>
                </div>
                <div className="font-display font-bold text-slate-900 mt-1 truncate">{q.customer_name}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{q.fault_description}</div>
                <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(q.created_at)}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-bold text-lg text-slate-900">{formatTL(q.target_total)}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setViewQuote(q)} data-testid={`view-${q.id}`}>
                <Eye className="h-3.5 w-3.5 mr-1" /> Müşteriye Göster
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadPDF(q)} data-testid={`pdf-${q.id}`}>
                <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
              </Button>
              <Button size="sm" variant="outline" onClick={() => whatsappShare(q)} data-testid={`wa-${q.id}`}>
                <Share2 className="h-3.5 w-3.5 mr-1" /> WhatsApp
              </Button>
              <Button size="sm" variant="ghost" className="text-rose-600 ml-auto" onClick={() => deleteQuote(q.id)} data-testid={`del-${q.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* WIZARD DIALOG */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Yeni Fiyat Teklifi — Adım {step}/3
            </DialogTitle>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Müşteri Adı *</Label>
                <Input data-testid="q-customer-name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Ahmet Yılmaz" />
              </div>
              <div>
                <Label>Müşteri Telefonu</Label>
                <Input data-testid="q-customer-phone" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} placeholder="05551112233" />
              </div>
              <div>
                <Label>Müşteriye Sunulacak Nihai Fiyat (₺) *</Label>
                <Input data-testid="q-target-total" type="number" value={form.target_total || ""} onChange={e => setForm({ ...form, target_total: e.target.value })} placeholder="10000" className="text-lg font-bold" />
                <div className="text-[10px] text-slate-500 mt-1">Bu tutar sabit kalacak. Sistem kalemleri bu tutara denk gelecek şekilde otomatik oranlayacak.</div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label>Arıza / İş Tanımı *</Label>
                <Textarea data-testid="q-fault" rows={2} value={form.fault_description} onChange={e => setForm({ ...form, fault_description: e.target.value })} placeholder="Kombi ana kart arızası" />
              </div>

              <div className="rounded-md bg-blue-50/50 border border-blue-100 p-3 space-y-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Cihaz Bilgileri (opsiyonel)</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Marka</Label>
                    <Select value={form.device_brand_name} onValueChange={(v) => setForm({ ...form, device_brand_name: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="q-brand"><SelectValue placeholder="Marka seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">-</SelectItem>
                        {brands.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Tip</Label>
                    <Select value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="q-type"><SelectValue placeholder="Tip seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">-</SelectItem>
                        <SelectItem value="Kombi">Kombi</SelectItem>
                        <SelectItem value="Klima">Klima</SelectItem>
                        <SelectItem value="Şofben">Şofben</SelectItem>
                        <SelectItem value="Kazan">Kazan</SelectItem>
                        <SelectItem value="Termosifon">Termosifon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Model</Label>
                    <Input data-testid="q-model" value={form.device_model} onChange={e => setForm({ ...form, device_model: e.target.value })} placeholder="Örn: 24 LCD" />
                  </div>
                  <div>
                    <Label className="text-xs">Seri No</Label>
                    <Input data-testid="q-serial" value={form.device_serial} onChange={e => setForm({ ...form, device_serial: e.target.value })} placeholder="Cihaz seri no" />
                  </div>
                </div>
              </div>

              <div>
                <Label>İşçilik Ücreti (₺) *</Label>
                <Input data-testid="q-labor" type="number" value={form.labor_fee || ""} onChange={e => setForm({ ...form, labor_fee: e.target.value })} placeholder="2000" />
                <div className="text-[10px] text-slate-500 mt-1">Hedef tutardan ne kadarı işçilik. Kalanı parçalara dağıtılacak.</div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Parça / Malzeme Kalemleri</Label>
                  <Button size="sm" variant="outline" onClick={addPart} data-testid="add-part"><Plus className="h-3 w-3 mr-1" /> Ekle</Button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {form.parts.map((p, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-7" placeholder={`Parça ${i + 1}`} value={p.name} onChange={e => updatePart(i, "name", e.target.value)} data-testid={`q-part-name-${i}`} />
                      <Input className="col-span-3" type="number" min={1} max={5} placeholder="Ağırlık" value={p.weight} onChange={e => updatePart(i, "weight", e.target.value)} data-testid={`q-part-weight-${i}`} />
                      <Button size="sm" variant="ghost" className="col-span-2 text-rose-600" onClick={() => removePart(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Ağırlık 1-5 arası — yüksek ağırlık daha pahalı parça anlamına gelir. Boş bırakırsanız eşit dağıtılır.</div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Önizleme</div>
                {preview.labor_fee > 0 && (
                  <div className="flex justify-between text-sm py-1">
                    <span>İşçilik</span>
                    <span className="font-mono">{formatTL(preview.labor_fee)}</span>
                  </div>
                )}
                {preview.parts.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm py-1">
                    <span className="truncate pr-2">{p.name}</span>
                    <span className="font-mono">{formatTL(p.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-base pt-2 mt-2 border-t border-slate-300">
                  <span>TOPLAM</span>
                  <span className="font-mono">{formatTL(form.target_total)}</span>
                </div>
              </div>
              <div>
                <Label>Alt Not (PDF ve ekranda görünür)</Label>
                <Textarea data-testid="q-footer-note" rows={2} value={form.footer_note} onChange={e => setForm({ ...form, footer_note: e.target.value })} placeholder="İndirim talepleriniz için lütfen Servis Müdürümüz ile iletişime geçiniz." />
                <div className="text-[10px] text-slate-500 mt-1">Boş bırakırsanız genel ayarlardaki varsayılan not kullanılır.</div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wizard-prev">Geri</Button>}
            {step < 3 && (
              <Button onClick={() => setStep(step + 1)} disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)} data-testid="wizard-next">
                İleri
              </Button>
            )}
            {step === 3 && <Button onClick={save} data-testid="wizard-save">Oluştur & Göster</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Müşteri Görünüm Modal */}
      {viewQuote && (
        <QuoteCustomerView
          quote={viewQuote}
          settings={settings}
          onClose={() => setViewQuote(null)}
          onDownload={() => downloadPDF(viewQuote)}
          onShare={() => whatsappShare(viewQuote)}
        />
      )}
    </div>
  );
}
